import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { hot } from 'react-hot-loader';
import axios from 'axios';

import ENDPOINTS from 'tradera-constants/endpoints';
import { handleError, API_ERRORS } from 'tradera-utils/api';
import { logger } from 'packages';
import { LAST_CHANCE } from '../content-types';
import { trackContainerClick } from '../analytics';
import {
    withTranslator,
    useTranslator,
    withUrlLocalizer
} from 'tradera-lang/translate';
import { registerLiveUpdates } from 'tradera-utils/register-live-updates';
import { ProviderWithGlobalStore } from 'tradera-state/configure-store';
import itemActions from 'tradera-state/items/actions';
import uiActions from 'tradera-state/ui/actions';
import { generateItemCardPlaceholders } from 'tradera-components/item-card/generate-item-card-placeholders';
import { lazyLoadSettings } from 'tradera-components/item-card/config';
import ItemCard from 'tradera-components/item-card/item-card';
import ApplicationErrorBoundary from 'tradera-components/application-error-boundary';
import useIsMobile from 'tradera-hooks/use-is-mobile';
import ALink from 'tradera-components/alink/alink';

const LastChance = props => (
    <ApplicationErrorBoundary>
        <ProviderWithGlobalStore>
            <LastChanceNewConnected {...props} />
        </ProviderWithGlobalStore>
    </ApplicationErrorBoundary>
);

const LastChanceNew = props => {
    const {
        addItems,
        initData,
        inSPA,
        isLoggedIn,
        items,
        openQuickView,
        preferredCurrency,
        toggleWishlist,
        toLocalizedUrl,
        updateItem
    } = props;
    const {
        BuyingEventPusherEnabled,
        BuyingEventPusherServerUrl
    } = initData.pageSpecificData;

    const [state, setState] = useState({
        loading: true,
        error: false,
        itemIds: []
    });
    const { loading, error, itemIds } = state;

    useEffect(() => {
        const { cancel, token: cancelToken } = axios.CancelToken.source();
        axios
            .get(ENDPOINTS.LAST_CHANCE, { cancelToken })
            .then(({ data }) => {
                let itemIds = [];
                if (data) {
                    addItems(data);
                    itemIds = data.map(item => item.itemId);
                }
                return setState({
                    loading: false,
                    error: false,
                    itemIds
                });
            })
            .catch(handleError())
            .catch(error => {
                switch (error.message) {
                    case API_ERRORS.CANCELLED:
                        return;
                    case API_ERRORS.ABORTED:
                    case API_ERRORS.NETWORK:
                        break;
                    default:
                        logger(error);
                }
                setState({
                    loading: false,
                    error: true,
                    itemIds: []
                });
            });
        return cancel;
    }, [addItems]);

    useEffect(() => {
        if (BuyingEventPusherEnabled) {
            return registerLiveUpdates({
                url: BuyingEventPusherServerUrl,
                onConnect: socket => {
                    for (let itemId of itemIds) {
                        socket.emit('join', `item:${itemId}`);
                    }
                },
                onClientEvent: data => {
                    switch (data.Type) {
                        case 'ItemSold':
                        case 'ItemUnSold':
                            break;
                        case 'AuctionLeadingBidUpdated':
                        case 'IAuctionLeadingBidUpdated':
                            updateItem({
                                itemId: data.Event.AuctionId,
                                item: {
                                    currentBid: data.Event.BidAmount,
                                    totalBids: data.Event.NumberOfBids
                                }
                            });
                            break;
                    }
                }
            });
        }
    }, [
        BuyingEventPusherEnabled,
        BuyingEventPusherServerUrl,
        itemIds,
        updateItem
    ]);

    const { t } = useTranslator();
    const itemCount = useIsMobile() ? 6 : 8;

    const handleQuickView = useCallback(
        itemId => {
            openQuickView(itemId, itemIds);
        },
        [itemIds, openQuickView]
    );

    if (error || (!loading && itemIds.length === 0)) return null;

    const resolveItems = (itemIds = [], itemsMap = {}, maxItemsCount = 0) => {
        const mappedItems = itemIds.map(itemId => itemsMap[itemId]);
        return mappedItems.filter(item => !!item).slice(0, maxItemsCount);
    };

    const displayItems = loading
        ? generateItemCardPlaceholders(itemCount)
        : resolveItems(itemIds, items, itemCount);

    return (
        <section className="pt-5 pt-md-3">
            <h3 className="start-page-section-title">
                {t('lastChance_header')}
            </h3>
            <div className="item-card-layout layout-grid ml-lg-1 ml-xl-0 mr-lg-n1 mr-xl-n2">
                {displayItems.map(item => (
                    <div key={item.itemId} className="item-card-container pb-2">
                        <ItemCard
                            animatePriceLabel={true}
                            animateTimeLeft={
                                initData.featureSwitches[
                                    'item-card-time-left-animation'
                                ]
                            }
                            isLoggedIn={isLoggedIn}
                            item={item}
                            lazy={lazyLoadSettings}
                            onAddToWishlist={toggleWishlist}
                            onQuickView={handleQuickView}
                            onRemoveFromWishlist={toggleWishlist}
                            preferredCurrency={preferredCurrency}
                            ready={!!item.itemUrl}
                        />
                    </div>
                ))}
            </div>
            <div className="row">
                <div className="col-12 text-center">
                    <ALink
                        useLink={inSPA}
                        href={toLocalizedUrl('/campaign/sista-chansen')}
                        role="button"
                        className="btn btn-primary btn-wide"
                        onClick={() =>
                            trackContainerClick(
                                LAST_CHANCE,
                                'Last chance button below'
                            )
                        }>
                        {`${t('lastChance_showMore')} ->`}
                    </ALink>
                </div>
            </div>
        </section>
    );
};

const mapStateToProps = state => ({
    initData: state.initData,
    items: state.items.cache,
    isLoggedIn: state.member.isLoggedIn,
    preferredCurrency: state.multiCurrency.preferredCurrency
});

const mapDispatchToProps = dispatch => ({
    addItems: items => {
        dispatch(itemActions.add(items));
    },
    openQuickView: (itemId, itemIds) =>
        dispatch(
            uiActions.openQuickView({
                itemIds,
                itemId,
                openedFrom: 'Last Chance'
            })
        ),
    toggleWishlist: itemId => dispatch(itemActions.wishlist(itemId)),
    updateItem: payload => dispatch(itemActions.updateItem(payload))
});

const LastChanceNewConnected = connect(
    mapStateToProps,
    mapDispatchToProps
)(LastChanceNew);

export default compose(
    withUrlLocalizer,
    withTranslator,
    hot(module)
)(LastChance);
